import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import ProtectedRoute from "../Components/ProtectedRoute";
import { Search, Eye, Download, BookOpen } from "lucide-react";

export default function GeneralLedger() {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    const [a, s] = await Promise.all([
      fetch("/api/ledger", {
        credentials: "include",
      }),
      fetch("/api/ledger/summary", {
        credentials: "include",
      }),
    ]);

    const accountsData = await a.json();
    const summaryData = await s.json();

    if (accountsData.status === "success") setAccounts(accountsData.data);

    if (summaryData.status === "success") setSummary(summaryData.data);
  }

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return accounts;

    return accounts.filter(
      (a) =>
        (a.name || "").toLowerCase().includes(term) ||
        (a.code || "").toLowerCase().includes(term) ||
        (a.type || "").toLowerCase().includes(term),
    );
  }, [accounts, search]);

  return (
    <ProtectedRoute>
      <DashboardLayout>
        <div className="space-y-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-serif">General Ledger</h1>

              <p className="text-sm text-gray-400 mt-1">
                Ledger generated automatically from Journal Entries
              </p>
            </div>

            <button className="flex items-center gap-2 px-5 py-2 rounded-xl bg-amber-400 text-black">
              <Download size={18} />
              Export
            </button>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <Kpi title="Ledger Accounts" value={summary.accounts || 0} />
            <Kpi title="Assets" value={summary.assets || 0} />
            <Kpi title="Income" value={summary.income || 0} />
            <Kpi title="Expenses" value={summary.expense || 0} />
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <div className="relative w-80">
              <Search
                size={16}
                className="absolute left-3 top-3 text-gray-500"
              />

              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search account"
                className="pl-10 pr-4 py-2 rounded-xl bg-black/40 border border-white/10 w-full"
              />
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden bg-white/5 border border-white/10">
            <table className="w-full">
              <thead className="bg-black/40 text-gray-400">
                <tr>
                  <th className="px-6 py-4 text-left">Account</th>

                  <th>Type</th>

                  <th>Opening</th>

                  <th>Debit</th>

                  <th>Credit</th>

                  <th>Closing</th>

                  <th></th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((a) => {
                  const closing = a.opening + a.debit - a.credit;

                  return (
                    <tr
                      key={a.id}
                      className="border-t border-white/10 hover:bg-white/5"
                    >
                      <td className="px-6 py-4 flex items-center gap-2">
                        <BookOpen size={16} />

                        {a.name}
                      </td>

                      <td>{a.type}</td>

                      <td>₹{a.opening.toLocaleString()}</td>

                      <td className="text-emerald-400">
                        ₹{a.debit.toLocaleString()}
                      </td>

                      <td className="text-rose-400">
                        ₹{a.credit.toLocaleString()}
                      </td>

                      <td>₹{closing.toLocaleString()}</td>

                      <td>
                        <button
                          onClick={() => setSelected(a)}
                          className="hover:text-amber-400"
                        >
                          <Eye size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {selected && (
            <LedgerModal account={selected} onClose={() => setSelected(null)} />
          )}
        </div>
      </DashboardLayout>
    </ProtectedRoute>
  );
}

function LedgerModal({ account, onClose }) {
  const [rows, setRows] = useState([]);
  const [opening, setOpening] = useState(0);
  const [closing, setClosing] = useState(0);

  useEffect(() => {
    fetch(`/api/ledger/${account.id}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.status === "success") {
          setRows(d.transactions);
          setOpening(d.opening);
          setClosing(d.closing);
        }
      });
  }, [account]);

  let running = account.opening;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="w-full max-w-6xl rounded-2xl bg-[#09090B] border border-white/10">
        <div className="flex justify-between items-center px-8 py-5 border-b border-white/10">
          <div>
            <h2 className="text-2xl font-serif">{account.name} Ledger</h2>

            <p className="text-xs text-gray-400">Transaction History</p>
          </div>

          <button onClick={onClose} className="text-xl">
            ✕
          </button>
        </div>

        <div className="grid grid-cols-4 gap-6 p-8">
          <SummaryCard title="Opening" value={opening} />

          <SummaryCard
            title="Debit"
            value={rows.reduce((s, r) => s + Number(r.debit), 0)}
          />

          <SummaryCard
            title="Credit"
            value={rows.reduce((s, r) => s + Number(r.credit), 0)}
          />

          <SummaryCard title="Closing" value={closing} />
        </div>

        <div className="px-8 pb-8">
          <table className="w-full">
            <thead className="text-gray-400">
              <tr>
                <th className="text-left py-3">Date</th>

                <th className="text-left">Voucher</th>

                <th className="text-left">Description</th>

                <th>Debit</th>

                <th>Credit</th>

                <th>Running Balance</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.date}</td>
                  <td>{r.voucher}</td>
                  <td>{r.description}</td>
                  <td className="text-emerald-400">
                    ₹{Number(r.debit).toLocaleString("en-IN")}
                  </td>
                  <td className="text-rose-400">
                    ₹{Number(r.credit).toLocaleString("en-IN")}
                  </td>
                  <td>₹{Number(r.balance).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Kpi({ title, value }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
      <p className="text-xs uppercase tracking-widest text-gray-400">{title}</p>

      <h2 className="mt-3 text-3xl font-serif">{value}</h2>
    </div>
  );
}

function SummaryCard({ title, value }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-5">
      <p className="text-xs uppercase tracking-widest text-gray-400">{title}</p>

      <h3 className="text-2xl mt-3 font-serif">
        ₹{Number(value).toLocaleString()}
      </h3>
    </div>
  );
}
